import { useCallback, useEffect, useMemo, useState } from 'react'
import { ApiError, api, can, refusalReason, tierLabel, type Vendor } from '../../lib/api'
import { baselines, type GroupMember, type VendorGroup } from '../../lib/baselines'
import { ErrorBox, Loading } from '../../components/DataTable'
import { Status } from '../../components/kit'
import { ReasonTip } from '../../components/vendor/ReasonTip'
import '../../styles/baselines.css'

// Settings, Vendor groups: named lists of vendors (release 3.4, M7).
//
// # WHY THIS SCREEN EXISTS
//
// "Vendor category" is free text and cannot drive a rule, and the register had
// no groups at all (12 Oct plan, 2.6). A group is a static list somebody keeps:
// the payment processors, the group companies, the call-centre vendors. The
// baseline rules are written against them, and the bulk send chooses by them.
// A group is retired rather than deleted, and a vendor taken out is flagged,
// so what a group held on a given day can still be read (migration 0115).
//
// The list is on the left of the pane and the open group's members beside it
// (?g=<id> in the address), so a reader keeps their place.

function asApiError(err: unknown): ApiError {
  return err instanceof ApiError ? err : new ApiError(0, 'UNKNOWN', String(err))
}

function groupFromHash(): string {
  const q = window.location.hash.split('?')[1] ?? ''
  return new URLSearchParams(q).get('g') ?? ''
}

function openGroup(id: string) {
  const base = window.location.hash.split('?')[0] || '#/settings'
  const q = new URLSearchParams(window.location.hash.split('?')[1] ?? '')
  if (!q.get('t')) q.set('t', 'vendor-groups')
  if (id) q.set('g', id)
  else q.delete('g')
  window.location.hash = (base + '?' + q.toString()).replace(/^#/, '')
}

export function VendorGroupsSettings() {
  const [groups, setGroups] = useState<VendorGroup[] | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [open, setOpen] = useState(groupFromHash)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [adding, setAdding] = useState(false)
  const [addError, setAddError] = useState<ApiError | null>(null)
  const mayWrite = can('config.write')

  const load = useCallback(() => {
    baselines
      .groups()
      .then((g) => {
        setGroups(g.groups)
        setError(null)
      })
      .catch(setError)
  }, [])
  useEffect(load, [load])
  useEffect(() => {
    const on = () => setOpen(groupFromHash())
    window.addEventListener('hashchange', on)
    return () => window.removeEventListener('hashchange', on)
  }, [])

  function add() {
    setAdding(true)
    setAddError(null)
    baselines
      .createGroup(name, description)
      .then((r) => {
        setName('')
        setDescription('')
        load()
        openGroup(r.group.id)
      })
      .catch((err) => setAddError(asApiError(err)))
      .finally(() => setAdding(false))
  }

  if (error) return <ErrorBox error={error} what="The vendor groups" />
  if (!groups) return <Loading what="the vendor groups" />

  return (
    <section className="panel bl-panel">
      <div className="bl-head">
        <div>
          <h2>Vendor groups</h2>
          <p className="muted">Named lists of vendors. Baseline rules and the bulk send choose by them.</p>
        </div>
        {!mayWrite && (
          <span className="sw-row">
            <Status tone="neutral">Read only</Status>
            <ReasonTip label="Why you cannot change the groups" text={refusalReason('config.write')} />
          </span>
        )}
      </div>

      <div className="bl-split">
        <div className="bl-list">
          {groups.length === 0 ? (
            <p className="muted">No group yet.</p>
          ) : (
            <ul role="list" className="bl-groups">
              {groups.map((g) => (
                <li key={g.id}>
                  <a
                    href={'#/settings?t=vendor-groups&g=' + encodeURIComponent(g.id)}
                    aria-current={open === g.id ? 'true' : undefined}
                    className="bl-group-link"
                  >
                    <strong>{g.name}</strong>
                    <span className="cell-sub">
                      {g.members} vendor{g.members === 1 ? '' : 's'}
                      {g.rules > 0 ? ', in ' + g.rules + ' rule' + (g.rules === 1 ? '' : 's') : ''}
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          )}
          {mayWrite && (
            <form
              className="fieldset bl-add-group"
              aria-label="Add a group"
              onSubmit={(e) => {
                e.preventDefault()
                if (name.trim()) add()
              }}
            >
              {addError && <ErrorBox error={addError} what="The group" />}
              <div className="field">
                <label htmlFor="vg-name">New group</label>
                <input id="vg-name" value={name} maxLength={80} onChange={(e) => setName(e.target.value)} placeholder="Payment processors" />
              </div>
              <div className="field">
                <label htmlFor="vg-desc">What it is for (optional)</label>
                <input id="vg-desc" value={description} maxLength={500} onChange={(e) => setDescription(e.target.value)} />
              </div>
              <button type="submit" className="primary" disabled={adding || !name.trim()}>
                {adding ? 'Adding' : 'Add group'}
              </button>
            </form>
          )}
        </div>

        <div className="bl-detail">
          {open ? (
            <GroupDetail key={open} id={open} mayWrite={mayWrite} onChanged={load} />
          ) : (
            <p className="muted">Choose a group to see its vendors.</p>
          )}
        </div>
      </div>
    </section>
  )
}

function GroupDetail({ id, mayWrite, onChanged }: { id: string; mayWrite: boolean; onChanged: () => void }) {
  const [group, setGroup] = useState<VendorGroup | null>(null)
  const [members, setMembers] = useState<GroupMember[]>([])
  const [error, setError] = useState<unknown>(null)
  const [actionError, setActionError] = useState<ApiError | null>(null)
  const [busy, setBusy] = useState(false)
  const [renaming, setRenaming] = useState(false)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [picking, setPicking] = useState(false)

  const load = useCallback(() => {
    baselines
      .group(id)
      .then((r) => {
        setGroup(r.group)
        setMembers(r.members)
        setName(r.group.name)
        setDescription(r.group.description ?? '')
        setError(null)
      })
      .catch(setError)
  }, [id])
  useEffect(load, [load])

  function act(p: Promise<unknown>) {
    setBusy(true)
    setActionError(null)
    p.then(() => {
      load()
      onChanged()
    })
      .catch((err) => setActionError(asApiError(err)))
      .finally(() => setBusy(false))
  }

  if (error) return <ErrorBox error={error} what="The group" />
  if (!group) return <Loading what="the group" />
  const retired = Boolean(group.retiredAt)

  return (
    <div>
      <div className="bl-head">
        <div>
          <h3>{group.name}</h3>
          {group.description && <p className="muted">{group.description}</p>}
          {retired && <Status tone="neutral">Retired</Status>}
        </div>
        {mayWrite && !retired && (
          <div className="sw-row">
            <button type="button" onClick={() => setPicking((v) => !v)} aria-expanded={picking} disabled={busy}>
              Add vendors
            </button>
            <button type="button" onClick={() => setRenaming((v) => !v)} aria-expanded={renaming} disabled={busy}>
              Rename
            </button>
            <button
              type="button"
              className="danger"
              onClick={() => act(baselines.retireGroup(id).then(() => openGroup('')))}
              disabled={busy || group.rules > 0}
              title={group.rules > 0 ? 'A baseline rule names this group. Remove the rule first.' : undefined}
            >
              Retire
            </button>
          </div>
        )}
      </div>
      {actionError && <ErrorBox error={actionError} what="The change" />}

      {renaming && (
        <form
          className="fieldset"
          aria-label="Rename the group"
          onSubmit={(e) => {
            e.preventDefault()
            act(baselines.renameGroup(id, name, description).then(() => setRenaming(false)))
          }}
        >
          <div className="field">
            <label htmlFor="vg-rename">Name</label>
            <input id="vg-rename" value={name} maxLength={80} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="vg-redesc">What it is for</label>
            <input id="vg-redesc" value={description} maxLength={500} onChange={(e) => setDescription(e.target.value)} />
          </div>
          <button type="submit" className="primary" disabled={busy || !name.trim()}>
            Save
          </button>
        </form>
      )}

      {picking && <MemberPicker already={members.map((m) => m.vendorId)} busy={busy} onAdd={(ids) => act(baselines.addMembers(id, ids).then(() => setPicking(false)))} />}

      {members.length === 0 ? (
        <p className="muted">No vendor in this group yet.</p>
      ) : (
        <div className="table-wrap">
          <table className="grid">
            <caption className="vh">The vendors in {group.name}.</caption>
            <thead>
              <tr>
                <th scope="col">Vendor</th>
                <th scope="col">Tier</th>
                <th scope="col">Added by</th>
                {mayWrite && !retired && (
                  <th scope="col">
                    <span className="vh">Take out</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.vendorId}>
                  <td>
                    <a href={'#/vendor/' + encodeURIComponent(m.vendorId)}>{m.name}</a>
                    <span className="cell-sub mono">{m.vendorCode}</span>
                  </td>
                  <td>{m.tier ? tierLabel(m.tier) : <span className="muted">no tier</span>}</td>
                  <td className="mono">{m.addedBy}</td>
                  {mayWrite && !retired && (
                    <td>
                      <button type="button" className="linkish" aria-label={'Take ' + m.name + ' out'} onClick={() => act(baselines.removeMember(id, m.vendorId))} disabled={busy}>
                        Take out
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function MemberPicker({ already, busy, onAdd }: { already: string[]; busy: boolean; onAdd: (ids: string[]) => void }) {
  const [vendors, setVendors] = useState<Vendor[] | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [q, setQ] = useState('')
  const [picked, setPicked] = useState<string[]>([])
  useEffect(() => {
    api
      .vendors({})
      .then((p) => setVendors(p.vendors))
      .catch(setError)
  }, [])
  const matches = useMemo(() => {
    const t = q.trim().toLowerCase()
    return (vendors ?? [])
      .filter((v) => !already.includes(v.id) && v.status !== 'OFFBOARDED' && v.status !== 'REJECTED')
      .filter((v) => !t || (v.displayName || v.legalName).toLowerCase().includes(t) || v.vendorCode.toLowerCase().includes(t))
      .slice(0, 50)
  }, [vendors, q, already])

  if (error) return <ErrorBox error={error} what="The vendor list" />
  if (!vendors) return <Loading what="the vendor list" />
  return (
    <div className="fieldset bl-picker">
      <div className="field">
        <label htmlFor="vg-find">Find vendors</label>
        <input id="vg-find" type="search" value={q} placeholder="Name or vendor code" onChange={(e) => setQ(e.target.value)} />
      </div>
      <ul role="list" className="pickbox">
        {matches.map((v) => (
          <li key={v.id}>
            <label>
              <input
                type="checkbox"
                checked={picked.includes(v.id)}
                onChange={() => setPicked((p) => (p.includes(v.id) ? p.filter((x) => x !== v.id) : [...p, v.id]))}
              />{' '}
              {v.displayName || v.legalName} <span className="mono muted">{v.vendorCode}</span>
            </label>
          </li>
        ))}
        {matches.length === 0 && <li className="muted">No vendor matches.</li>}
      </ul>
      <button type="button" className="primary" disabled={busy || picked.length === 0} onClick={() => onAdd(picked)}>
        Add {picked.length || ''} to the group
      </button>
    </div>
  )
}
